import { confirmSessionEnded, type SessionProbe } from "./sessionProbe";

type ErrorShape = { status?: unknown; name?: unknown };

const shapeOf = (error: unknown): ErrorShape =>
  typeof error === "object" && error !== null ? (error as ErrorShape) : {};

/** supabase-js found no session in the browser at all: nothing to confirm. */
const isMissingSession = (error: ErrorShape) =>
  error.status === 400 && error.name === "AuthSessionMissingError";

/**
 * Decides whether an error from a direct Supabase call (PostgREST through the
 * react-admin dataProvider, or Supabase Auth) must end the CRM session.
 *
 * ra-supabase signs out on any 401 or 403. A PostgREST 403 is usually a
 * verdict on one statement, not on the session: an RLS policy or a revoked
 * function (SQLSTATE 42501) while the operator's session is fine. Signing out
 * there hides the real error and loses the operator's work, which is how the
 * CRM's Create agency dialog logged people out (TRI-59). So a 401 or 403 only
 * ends the session once Supabase confirms the session is gone.
 */
export async function supabaseErrorEndsSession(
  error: unknown,
  probeSession: SessionProbe,
): Promise<boolean> {
  const shape = shapeOf(error);
  if (isMissingSession(shape)) return true;
  if (shape.status !== 401 && shape.status !== 403) return false;
  return confirmSessionEnded(probeSession);
}
